import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';

const STATUS_FLOW = ['pending', 'packed', 'dispatched', 'delivered'];

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [assign, setAssign] = useState({ staffId: '', vehicleId: '' });

  const fetch = useCallback(async () => {
    try {
      const orderRes = await api.get(`/orders/${id}`);
      setOrder(orderRes.data.data);

      if (user?.role === 'ADM' || user?.role === 'MGR') {
        const [staffRes, vehicleRes] = await Promise.all([
          api.get('/staff'),
          api.get('/vehicles'),
        ]);
        setStaff(staffRes.data.data);
        setVehicles(vehicleRes.data.data);
      }
    } catch { toast('Failed to load order', 'error'); }
    finally { setLoading(false); }
  }, [id, toast, user?.role]);

  useEffect(() => { fetch(); }, [fetch]);

  const updateStatus = async (status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      toast(`Status → ${status}`, 'success');
      fetch();
    } catch { toast('Failed to update status', 'error'); }
  };

  const viewInvoice = () => {
    window.open(`${api.defaults.baseURL}/orders/${id}/invoice`, '_blank');
  };

  const handleAssign = async () => {
    if (!assign.staffId) { toast('Select a staff member', 'error'); return; }
    try {
      await api.post('/staff/assign', { staffId: assign.staffId, orderId: id, vehicleId: assign.vehicleId || null });
      toast('Staff assigned!', 'success');
      fetch();
    } catch { toast('Failed to assign', 'error'); }
  };

  if (loading) return <div className="flex-center" style={{ height: 300 }}><div className="spinner"></div></div>;
  if (!order) return <div className="empty-state"><div className="empty-icon">⚠️</div><p>Order not found</p></div>;

  const items = Array.isArray(order.items) ? order.items : [];

  const statusColors = { pending: '#facc15', packed: '#16a34a', dispatched: '#38bdf8', delivered: '#22c55e', cancelled: '#ef4444' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/orders')}>← Back</button>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700 }}>Order #{order.id.slice(0, 8).toUpperCase()}</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            {new Date(order.created_at).toLocaleString('en-IN')}
          </p>
        </div>
        {order.invoice_url && (
          <button className="btn btn-primary btn-sm" onClick={viewInvoice} style={{ marginRight: 10 }}>
            📄 View Invoice PDF
          </button>
        )}
        <span className={`badge badge-${order.order_status}`} style={{ fontSize: 13, padding: '6px 14px' }}>
          {order.order_status}
        </span>
      </div>

      <div className="resp-grid-2">
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Customer info */}
          <div className="card">
            <div className="card-header"><span className="card-title">👤 Customer Details</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'Name', value: order.customer_name },
                { label: 'Phone', value: order.phone },
                { label: 'Address', value: order.address },
                { label: 'Apartment', value: order.apartment_name || '—' },
                { label: 'Payment', value: order.payment_method?.toUpperCase() },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', gap: 12 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 70, flexShrink: 0 }}>{row.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Update status */}
          <div className="card">
            <div className="card-header"><span className="card-title">📋 Update Status</span></div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {STATUS_FLOW.map(s => (
                <button
                  key={s}
                  className={`btn btn-sm ${order.order_status === s ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => updateStatus(s)}
                  style={{ textTransform: 'capitalize', borderLeft: `3px solid ${statusColors[s]}` }}
                >
                  {order.order_status === s && '✓ '}{s}
                </button>
              ))}
              <button className="btn btn-danger btn-sm" onClick={() => updateStatus('cancelled')}>Cancel</button>
            </div>
          </div>

          {/* Assign staff */}
          {user?.role !== 'STF' && (
            <div className="card">
              <div className="card-header"><span className="card-title">👥 Assign Delivery</span></div>
              {order.staff_name && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--dark-3)', borderRadius: 8, fontSize: 13 }}>
                  ✅ Assigned to <strong>{order.staff_name}</strong> {order.vehicle_number && `· 🚚 ${order.vehicle_number}`}
                </div>
              )}
              <div className="form-grid" style={{ gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Staff Member</label>
                  <select className="form-select" value={assign.staffId} onChange={e => setAssign(p => ({ ...p, staffId: e.target.value }))}>
                    <option value="">Select staff...</option>
                    {staff.filter(s => s.role === 'STF').map(s => (
                      <option key={s.id} value={s.id}>{s.full_name || s.username}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Vehicle</label>
                  <select className="form-select" value={assign.vehicleId} onChange={e => setAssign(p => ({ ...p, vehicleId: e.target.value }))}>
                    <option value="">Select vehicle...</option>
                    {vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_number} ({v.type})</option>)}
                  </select>
                </div>
                <button className="btn btn-primary btn-sm" onClick={handleAssign}>Assign</button>
              </div>
            </div>
          )}
        </div>

        {/* Right column - Items */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">🛒 Order Items</span>
            <span style={{ fontWeight: 700, fontSize: 16 }}>₹{parseFloat(order.total_amount).toLocaleString('en-IN')}</span>
          </div>
          {items.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
              {item.imageUrl ? (
                <img src={item.imageUrl} alt={item.name} className="product-img" />
              ) : (
                <div className="product-img-placeholder">🥬</div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{item.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>₹{item.price} × {item.quantity}</div>
              </div>
              <div style={{ fontWeight: 700 }}>₹{item.subtotal?.toFixed(2)}</div>
            </div>
          ))}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', paddingTop: 12, borderTop: '2px solid var(--border)' }}>
            <span style={{ fontWeight: 600 }}>Total</span>
            <span style={{ fontWeight: 800, fontSize: 18 }}>₹{parseFloat(order.total_amount).toLocaleString('en-IN')}</span>
          </div>
          {order.notes && (
            <div style={{ marginTop: 14, padding: 12, background: 'var(--dark-3)', borderRadius: 8, fontSize: 13, color: 'var(--text-muted)' }}>
              📝 {order.notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
